import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  documentsApi,
  MedicalDocument,
  UploadFileError,
  UploadFileMeta,
  UploadFilePayload,
  UploadLinkEnvelope,
  UploadOcrMode,
  UploadResponse,
} from '../api/documents';
import { ModalFooter } from './Modal';
import CameraCapture from './CameraCapture';
import { formatApiError } from '../utils/errorUtils';
import { isCameraSupported } from '../utils/camera';
import {
  processDocumentLocally,
  LocalProcessProgress,
  NerAnonymizationOptions,
} from '../utils/localDocumentProcessing';
import { cryptoApi } from '../api/crypto';
import { authApi } from '../api/auth';
import {
  b64,
  importPublicJwk,
  wrapDekForUser,
  encryptFileE2EE,
} from '../utils/e2eeCrypto';
import { useIsAdvanced } from '../utils/viewMode';
import './DocumentUpload.css';

// ---------------------------------------------------------------------------
// File-format whitelist (matches the backend accept list). We check both
// MIME type and lowercase extension because browsers don't reliably set MIME
// for HEIC/HEIF/older Office formats.
// ---------------------------------------------------------------------------
const ALLOWED_EXTS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.tiff',
  '.tif',
  '.heic',
  '.heif',
  '.docx',
  '.txt',
  '.xlsx',
  '.csv',
] as const;

const ACCEPT_ATTR = ALLOWED_EXTS.join(',');

// Per-file and aggregate caps from the always-E2EE upload contract.
const MAX_BULK_FILES = 20;
const MAX_GROUP_FILES = 10;
const MAX_TOTAL_BYTES = 100 * 1024 * 1024; // 100 MB
const MAX_PER_FILE_BYTES = 50 * 1024 * 1024; // 50 MB per file

type FilePrepStatus = 'queued' | 'processing' | 'ready' | 'failed';

/** Per-row local state for a file that's been picked / captured but
 *  not yet uploaded. Plaintext bytes live in `file` until submission,
 *  then we drop them after the request resolves. */
interface FilePrep {
  /** Stable client-side id; survives reorders. */
  id: string;
  file: File;
  filename: string;
  size: number;
  mimeType: string;
  status: FilePrepStatus;
  /** Filled in once `processDocumentLocally` succeeds for this file. */
  ocr?: {
    markdown: string;
    confidence: number | null;
    language: string;
    pii_anonymized: boolean;
  };
  ciphertext?: Blob;
  /** Base64-encoded 12-byte IV used for AES-GCM. */
  iv?: string;
  envelopes?: Array<{ user_id: number; wrapped_dek: string }>;
  /** Per-active-share-link envelopes resolved at submit time. Held
   *  on the row so retry-failed re-uses the same set. Empty when the
   *  member has no active share links (the common case). */
  linkEnvelopes?: UploadLinkEnvelope[];
  /** Local pre-processing error (OCR / encrypt / wrap). */
  error?: string;
  /** Server's verdict for this row from the most recent submit. */
  serverStatus?: 'success' | 'error';
  serverMessage?: string;
}

interface DocumentUploadProps {
  memberId: number;
  /** Called after a successful (or partially successful) submit.
   *  The caller can use the returned documents to optimistically
   *  refresh list views. Existing call sites that ignore the
   *  argument keep working unchanged. */
  onSuccess: (documents: MedicalDocument[]) => void;
  onCancel: () => void;
}

let rowIdCounter = 0;
const makeRowId = (): string => {
  rowIdCounter += 1;
  return `f-${Date.now().toString(36)}-${rowIdCounter}`;
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isAllowedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ALLOWED_EXTS.some((ext) => name.endsWith(ext));
}

const NER_MODEL_BY_PRESET: Record<'multilingual' | 'ro-bert-pr', string> = {
  multilingual: 'Xenova/bert-base-multilingual-cased-ner-hrl',
  'ro-bert-pr': 'dumitrescustefan/bert-base-romanian-ner',
};

const DocumentUpload: React.FC<DocumentUploadProps> = ({ memberId, onSuccess, onCancel }) => {
  const isAdvanced = useIsAdvanced();
  const [items, setItems] = useState<FilePrep[]>([]);
  const [groupMode, setGroupMode] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [previewView, setPreviewView] = useState<'source' | 'preview'>('preview');
  // OCR mode default: `server` (matches the old modal's faster /
  // lower-friction default and the W-backend contract). When toggled
  // to `client` the user opts into browser-side OCR + NER for max
  // privacy at the cost of extra wait + model downloads.
  const [ocrMode, setOcrMode] = useState<UploadOcrMode>('server');
  // NER defaults: ON in advanced mode (matches the previous modal's
  // behaviour), OFF for simplified users so the slower model download
  // doesn't surprise newcomers. Only meaningful in `client` mode —
  // `server` mode skips browser OCR entirely so NER would be a no-op.
  const [nerEnabled, setNerEnabled] = useState<boolean>(isAdvanced);
  const [nerPreset, setNerPreset] = useState<'multilingual' | 'ro-bert-pr'>('multilingual');
  const [nerMinScore, setNerMinScore] = useState<number>(0.85);
  const [showNerAdvanced, setShowNerAdvanced] = useState<boolean>(false);
  const [nerRevision, setNerRevision] = useState<string>('refs/pr/2');
  const [nerDtype, setNerDtype] = useState<'fp32' | 'q8' | 'auto'>('fp32');
  const [nerModelFileName, setNerModelFileName] = useState<string>('model');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState<string>('');
  const [topError, setTopError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const formId = useId();

  // -------------------------------------------------------------------
  // Derived state.
  // -------------------------------------------------------------------
  const totalBytes = useMemo(() => items.reduce((acc, i) => acc + i.size, 0), [items]);
  const anyFailedLocal = items.some((i) => i.status === 'failed');
  const anyFailedServer = items.some((i) => i.serverStatus === 'error');
  const showFileList = items.length > 0;
  const showModeToggle = items.length >= 2;

  const validationError = useMemo<string | null>(() => {
    if (items.length === 0) return 'Selectează cel puțin un fișier.';
    if (groupMode && items.length > MAX_GROUP_FILES) {
      return `În modul "Un singur document" sunt permise cel mult ${MAX_GROUP_FILES} fișiere.`;
    }
    if (!groupMode && items.length > MAX_BULK_FILES) {
      return `În modul "Documente separate" sunt permise cel mult ${MAX_BULK_FILES} fișiere.`;
    }
    if (totalBytes > MAX_TOTAL_BYTES) {
      return `Dimensiunea totală (${formatSize(totalBytes)}) depășește limita de ${formatSize(MAX_TOTAL_BYTES)}.`;
    }
    return null;
  }, [items.length, groupMode, totalBytes]);

  const buildNerOptions = useCallback((): NerAnonymizationOptions | undefined => {
    if (!nerEnabled) return undefined;
    return {
      enabled: true,
      model: NER_MODEL_BY_PRESET[nerPreset],
      minScore: nerMinScore,
      revision: showNerAdvanced ? nerRevision : undefined,
      dtype: showNerAdvanced ? (nerDtype === 'auto' ? undefined : nerDtype) : undefined,
      model_file_name: showNerAdvanced ? nerModelFileName : undefined,
    };
  }, [nerEnabled, nerPreset, nerMinScore, showNerAdvanced, nerRevision, nerDtype, nerModelFileName]);

  // -------------------------------------------------------------------
  // Adding files (browse / drop / camera). Duplicates by name+size are
  // filtered so the same file added twice doesn't double-count toward
  // the limit. Per-file format / size checks surface immediately as a
  // top-of-modal alert instead of as failed rows.
  // -------------------------------------------------------------------
  const addFiles = useCallback((incoming: FileList | File[]) => {
    setTopError(null);
    setSuccessMsg(null);
    const list = Array.from(incoming);
    if (list.length === 0) return;

    setItems((prev) => {
      const existingKeys = new Set(prev.map((i) => `${i.filename}__${i.size}`));
      const next: FilePrep[] = prev.slice();
      const rejections: string[] = [];

      for (const file of list) {
        const key = `${file.name}__${file.size}`;
        if (existingKeys.has(key)) continue;
        existingKeys.add(key);

        if (!isAllowedFile(file)) {
          rejections.push(`${file.name}: format nesuportat`);
          continue;
        }
        if (file.size > MAX_PER_FILE_BYTES) {
          rejections.push(
            `${file.name}: depășește limita de ${formatSize(MAX_PER_FILE_BYTES)} pe fișier`
          );
          continue;
        }

        next.push({
          id: makeRowId(),
          file,
          filename: file.name,
          size: file.size,
          mimeType: file.type || 'application/octet-stream',
          status: 'queued',
        });
      }

      if (rejections.length > 0) {
        setTopError(`Câteva fișiere au fost respinse: ${rejections.join('; ')}.`);
      }
      return next;
    });
  }, []);

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setPreviewFileId((cur) => (cur === id ? null : cur));
  };

  const moveItem = (id: string, direction: -1 | 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx === -1) return prev;
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = prev.slice();
      const [moved] = next.splice(idx, 1);
      next.splice(targetIdx, 0, moved);
      return next;
    });
  };

  const patchItem = useCallback((id: string, patch: Partial<FilePrep>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  // -------------------------------------------------------------------
  // OCR / NER preview: run `processDocumentLocally` for a single row
  // without uploading. Used by the "Procesează" empty-state button so
  // the user can preview markdown before committing.
  // -------------------------------------------------------------------
  const runOcrForItem = useCallback(
    async (id: string) => {
      const target = items.find((i) => i.id === id);
      if (!target) return;
      patchItem(id, { status: 'processing', error: undefined });
      try {
        const ner = buildNerOptions();
        const result = await processDocumentLocally(
          target.file,
          (p: LocalProcessProgress) => {
            setProgressLabel(`OCR ${target.filename}: ${p.stage}`);
          },
          { ner }
        );
        patchItem(id, {
          status: 'ready',
          ocr: {
            markdown: result.markdown,
            confidence: result.ocrConfidence,
            language: result.languageUsed,
            pii_anonymized: !!ner?.enabled,
          },
          error: undefined,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Eroare la procesarea locală';
        patchItem(id, { status: 'failed', error: msg });
      } finally {
        setProgressLabel('');
      }
    },
    [items, buildNerOptions, patchItem]
  );

  // -------------------------------------------------------------------
  // Camera takeover. New W-camera contract: onDone(File[]) hands back
  // the captured shots; we append them to the file list and return to
  // the form view. onCancel just exits the takeover.
  // -------------------------------------------------------------------
  const handleCameraDone = useCallback(
    (shots: File[]) => {
      setShowCamera(false);
      if (shots.length) addFiles(shots);
    },
    [addFiles]
  );

  const cameraMaxShots = useMemo(() => {
    const remainingBulk = Math.max(0, MAX_BULK_FILES - items.length);
    return Math.min(10, remainingBulk || 10);
  }, [items.length]);

  // -------------------------------------------------------------------
  // Match the upload response back to client rows. The server identifies
  // files by `original_filename`; bulk uploads pair 1:1, group uploads
  // collect every file into `documents[0].files[]`. A `consumed` set
  // ensures duplicate filenames each light up exactly once.
  // -------------------------------------------------------------------
  const applyUploadResponse = useCallback(
    (response: UploadResponse, sentFilenames: string[]) => {
      const successFilenames: string[] = [];
      for (const d of response.documents) {
        for (const f of d.files || []) {
          if (f.original_filename) successFilenames.push(f.original_filename);
        }
      }
      const errorMap = new Map<string, string>();
      for (const e of response.errors || []) {
        errorMap.set(e.filename, e.message);
      }

      setItems((prev) => {
        const successRemaining = successFilenames.slice();
        const sentSet = new Set(sentFilenames);
        return prev.map((row) => {
          if (!sentSet.has(row.filename)) return row;
          const errMsg = errorMap.get(row.filename);
          if (errMsg) {
            return { ...row, serverStatus: 'error', serverMessage: errMsg };
          }
          const succIdx = successRemaining.indexOf(row.filename);
          if (succIdx !== -1) {
            successRemaining.splice(succIdx, 1);
            return {
              ...row,
              serverStatus: 'success',
              serverMessage: undefined,
            };
          }
          // Server didn't mention this file — leave it as it was (a
          // subsequent "Retrimite eșuate" will retry it).
          return row;
        });
      });
    },
    []
  );

  // -------------------------------------------------------------------
  // The main submit pipeline.
  //
  // Two modes (set via the OCR-mode toggle in Setări avansate):
  //
  // `server` (default):
  //   1) Skip browser OCR / NER entirely — the server does OCR once
  //      on the plaintext bytes and discards them immediately.
  //   2) Encrypt with AES-GCM (`encryptFileE2EE`) so the on-disk
  //      copy is always ciphertext.
  //   3) Wrap the DEK per user envelope.
  //   4) `files_meta[i].ocr_markdown = null`; send ciphertext under
  //      `files` AND plaintext under `files_plain`.
  //
  // `client`:
  //   1) Run `processDocumentLocally` (optionally with NER) per file.
  //   2) Encrypt + wrap as above.
  //   3) `files_meta[i].ocr_markdown = <markdown>`; send ciphertext
  //      only — plaintext NEVER leaves the device.
  //
  // After preparation we POST one multipart request to
  // `/api/documents/upload`. Per-file failures during pre-processing
  // never block the rest of the batch — they show ⚠ in the row.
  // -------------------------------------------------------------------
  const submitItems = useCallback(
    async (onlyIds: string[] | null) => {
      setTopError(null);
      setSuccessMsg(null);
      setProgress(0);
      setProgressLabel('Pregătire chei…');
      setSubmitting(true);

      // Snapshot the targets up front so we don't re-read state on each
      // setItems call below (state updates are async).
      const snapshot = items;
      const targets: FilePrep[] = onlyIds
        ? snapshot.filter((i) => onlyIds.includes(i.id))
        : snapshot.slice();

      if (targets.length === 0) {
        setSubmitting(false);
        setProgressLabel('');
        return;
      }

      try {
        // ----- 1) Resolve recipients ONCE per submission. ---------
        const myKeys = await cryptoApi.getMyKeys();
        if (!myKeys) {
          throw new Error(
            'Cheile E2EE lipsesc pentru contul tău. Re-autentifică-te pentru a finaliza configurarea criptării.'
          );
        }
        let myPublicJwk: JsonWebKey;
        try {
          myPublicJwk = JSON.parse(myKeys.public_jwk);
        } catch {
          throw new Error('Cheia publică stocată pentru cont este invalidă.');
        }
        const me = await authApi.getMe();
        const sharedIds = await cryptoApi.getMemberAccessUsers(memberId);
        const allIds = sharedIds.includes(me.id) ? sharedIds.slice() : [...sharedIds, me.id];
        const pubKeyRows = await cryptoApi.getPublicKeys(allIds);
        const pubById = new Map<number, JsonWebKey>();
        for (const r of pubKeyRows) {
          try {
            pubById.set(r.user_id, JSON.parse(r.public_jwk));
          } catch {
            // skip malformed entries; the missing-key check below will catch them
          }
        }
        pubById.set(me.id, myPublicJwk);

        const missing = allIds.filter((uid) => !pubById.has(uid));
        if (missing.length) {
          throw new Error(
            `Unii utilizatori partajați nu au cheie E2EE: ${missing.join(
              ', '
            )}. Cere-le să se autentifice și să-și finalizeze configurarea criptării, apoi încearcă din nou.`
          );
        }

        // Import RSA public keys once; reuse them per file below.
        const importedPubs: Array<{ user_id: number; key: CryptoKey }> = [];
        for (const uid of allIds) {
          const jwk = pubById.get(uid);
          if (!jwk) continue;
          importedPubs.push({ user_id: uid, key: await importPublicJwk(jwk) });
        }

        // ----- 2) Per-file: optional OCR + encrypt + wrap. --------
        const isClientMode = ocrMode === 'client';
        const ner = isClientMode ? buildNerOptions() : undefined;
        const prepFloor = 5;
        const prepCeiling = 65;
        const span = prepCeiling - prepFloor;

        const preparedBlobs: UploadFilePayload[] = [];
        const preparedPlain: UploadFilePayload[] = [];
        const preparedMeta: UploadFileMeta[] = [];
        const localFailures: string[] = [];

        for (let idx = 0; idx < targets.length; idx++) {
          const it = targets[idx];

          // --- Browser-OCR only in `client` mode. In `server` mode
          //     we skip it entirely; the server runs OCR on the
          //     plaintext we forward in `files_plain`.
          if (isClientMode && !it.ocr) {
            setProgressLabel(`OCR ${it.filename} (${idx + 1}/${targets.length})…`);
            patchItem(it.id, { status: 'processing', error: undefined });
            try {
              const result = await processDocumentLocally(it.file, undefined, { ner });
              it.ocr = {
                markdown: result.markdown,
                confidence: result.ocrConfidence,
                language: result.languageUsed,
                pii_anonymized: !!ner?.enabled,
              };
            } catch (e) {
              const msg = e instanceof Error ? e.message : 'Pre-procesare eșuată';
              patchItem(it.id, { status: 'failed', error: msg });
              localFailures.push(it.filename);
              setProgress(Math.round(prepFloor + (span * (idx + 1)) / targets.length));
              continue;
            }
          }

          // --- Encrypt + wrap DEK per recipient.
          setProgressLabel(`Criptare ${it.filename} (${idx + 1}/${targets.length})…`);
          try {
            const { dek, ciphertext, iv } = await encryptFileE2EE(it.file);
            const envelopes: Array<{ user_id: number; wrapped_dek: string }> = [];
            for (const { user_id, key } of importedPubs) {
              const wrapped = await wrapDekForUser(key, dek);
              envelopes.push({ user_id, wrapped_dek: b64.bytesToB64(wrapped) });
            }
            const ivB64 = b64.bytesToB64(iv);
            const blob = new Blob([ciphertext.buffer as ArrayBuffer], {
              type: 'application/octet-stream',
            });

            // Link envelopes carry through the FilePrep state. We
            // don't compute them here yet — the uploader rarely has
            // raw share-link tokens at upload time, so they stay []
            // and the owner-side `ensureLinkEnvelopesForMemberInvite`
            // flow materialises them later. The field is still on
            // the row so retry-failed re-uses the same value.
            const linkEnvelopes: UploadLinkEnvelope[] = it.linkEnvelopes ?? [];

            preparedBlobs.push({ blob, filename: it.filename });
            if (!isClientMode) {
              // server mode: forward the original plaintext too so
              // the backend can run OCR once + discard. Same
              // filename so the server can pair ciphertext to
              // plaintext positionally + by name.
              preparedPlain.push({ blob: it.file, filename: it.filename });
            }
            preparedMeta.push({
              original_filename: it.filename,
              mime_type: it.mimeType || 'application/octet-stream',
              size_bytes: it.size,
              iv: ivB64,
              envelopes,
              link_envelopes: linkEnvelopes,
              ocr_markdown: isClientMode ? (it.ocr?.markdown ?? null) : null,
              ocr_confidence: isClientMode ? (it.ocr?.confidence ?? null) : null,
              ocr_language: isClientMode ? (it.ocr?.language ?? null) : null,
              pii_anonymized: isClientMode ? !!it.ocr?.pii_anonymized : false,
            });
            patchItem(it.id, {
              status: 'ready',
              ocr: it.ocr,
              ciphertext: blob,
              iv: ivB64,
              envelopes,
              linkEnvelopes,
              error: undefined,
            });
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'Eroare la criptarea fișierului';
            patchItem(it.id, { status: 'failed', error: msg });
            localFailures.push(it.filename);
          }

          setProgress(Math.round(prepFloor + (span * (idx + 1)) / targets.length));
        }

        if (preparedBlobs.length === 0) {
          setTopError(
            'Niciun fișier nu a putut fi pregătit pentru încărcare. Verifică detaliile lângă fiecare rând.'
          );
          return;
        }

        if (localFailures.length > 0) {
          setTopError(
            `${localFailures.length} fișier${
              localFailures.length === 1 ? '' : 'e'
            } nu au putut fi pregătite. Vezi detalii lângă fiecare.`
          );
        }

        // ----- 3) POST the prepared ciphertext + meta. -------------
        setProgressLabel('Se încarcă pe server…');
        const trimmedTitle = groupTitle.trim();
        const response = await documentsApi.uploadDocument({
          memberId,
          files: preparedBlobs,
          filesPlain: isClientMode ? undefined : preparedPlain,
          filesMeta: preparedMeta,
          ocrMode,
          group: groupMode,
          title: groupMode && trimmedTitle ? trimmedTitle : undefined,
          notes: notes.trim() || undefined,
          onProgress: (pct) => {
            // Upload progress occupies the upper portion of the bar.
            const scaled = prepCeiling + Math.round((100 - prepCeiling) * (pct / 100));
            setProgress(Math.max(prepCeiling, Math.min(100, scaled)));
          },
        });

        // ----- 4) Apply server verdict back to rows. ---------------
        applyUploadResponse(
          response,
          preparedMeta.map((m) => m.original_filename)
        );

        const errs: UploadFileError[] = response.errors || [];
        const docCount = response.documents.length;

        if (errs.length === 0 && localFailures.length === 0) {
          setSuccessMsg(
            groupMode
              ? docCount === 1
                ? '1 document grupat creat'
                : `${docCount} documente create`
              : docCount === 1
                ? '1 document încărcat'
                : `${docCount} documente încărcate`
          );
          if (response.documents.length > 0) onSuccess(response.documents);
        } else {
          if (errs.length) {
            setTopError((prev) => {
              const serverMsg = `${errs.length} fișier${
                errs.length === 1 ? '' : 'e'
              } cu eroare la server. Verifică lista de mai jos.`;
              return prev ? `${prev} ${serverMsg}` : serverMsg;
            });
          }
          if (response.documents.length > 0) onSuccess(response.documents);
        }
      } catch (err) {
        const detail =
          (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        const msg =
          formatApiError(detail) ||
          (err instanceof Error ? err.message : null) ||
          'Eroare la încărcarea documentelor. Încearcă din nou.';
        setTopError(msg);
      } finally {
        setSubmitting(false);
        setProgressLabel('');
      }
    },
    [
      items,
      memberId,
      groupMode,
      groupTitle,
      notes,
      ocrMode,
      buildNerOptions,
      patchItem,
      applyUploadResponse,
      onSuccess,
    ]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validationError) {
      setTopError(validationError);
      return;
    }
    await submitItems(null);
  };

  const handleRetryFailed = async () => {
    const ids = items
      .filter((i) => i.status === 'failed' || i.serverStatus === 'error')
      .map((i) => i.id);
    if (!ids.length) return;
    // Reset the failed rows so the pipeline re-runs OCR / encryption.
    setItems((prev) =>
      prev.map((p) =>
        ids.includes(p.id)
          ? {
              ...p,
              status: 'queued',
              error: undefined,
              serverStatus: undefined,
              serverMessage: undefined,
              ocr: undefined,
              ciphertext: undefined,
              iv: undefined,
              envelopes: undefined,
              linkEnvelopes: undefined,
            }
          : p
      )
    );
    await submitItems(ids);
  };

  const openFilePicker = () => inputRef.current?.click();

  // -------------------------------------------------------------------
  // Camera takeover renders in place of the form (kept inside the same
  // modal frame). The camera component itself is full-viewport; we
  // simply replace the form body until it resolves.
  // -------------------------------------------------------------------
  if (showCamera) {
    return (
      <CameraCapture
        maxShots={cameraMaxShots}
        onDone={handleCameraDone}
        onCancel={() => setShowCamera(false)}
      />
    );
  }

  // -------------------------------------------------------------------
  // Render.
  // -------------------------------------------------------------------
  return (
    <>
      <form id={formId} className="document-upload-form" onSubmit={handleSubmit} noValidate>
        {topError && (
          <div className="alert alert-error document-upload-alert" role="alert">
            {topError}
          </div>
        )}
        {successMsg && !topError && (
          <div className="alert alert-success document-upload-alert" role="status">
            {successMsg}
          </div>
        )}

        {/* A. SOURCE TILES ------------------------------------------ */}
        <section className="document-upload-section" aria-labelledby="upload-source-heading">
          <h3 id="upload-source-heading" className="document-upload-section-title">
            Adaugă fișiere
          </h3>
          <p className="document-upload-section-hint">
            Max {MAX_BULK_FILES} fișiere / {formatSize(MAX_TOTAL_BYTES)} total. Acceptate: PDF,
            imagini (JPG/PNG/TIFF/HEIC), DOCX, TXT, XLSX, CSV. Max. {formatSize(MAX_PER_FILE_BYTES)}{' '}
            per fișier.
          </p>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = '';
            }}
            className="document-upload-input-hidden"
            tabIndex={-1}
            aria-label="Selectează fișiere de pe dispozitiv"
            disabled={submitting}
          />

          <div className="document-upload-sources" role="group" aria-label="Sursă fișiere">
            <button
              type="button"
              className={`document-upload-source-tile${dragging ? ' is-drag' : ''}`}
              onClick={submitting ? undefined : openFilePicker}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
              }}
              disabled={submitting}
              aria-label="Răsfoiește fișiere"
            >
              <span className="document-upload-source-icon" aria-hidden>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </span>
              <strong>Răsfoiește fișiere</strong>
              <span className="document-upload-source-hint">
                Trage și plasează sau apasă pentru a selecta
              </span>
            </button>

            {isCameraSupported() && (
              <button
                type="button"
                className="document-upload-source-tile"
                onClick={() => setShowCamera(true)}
                disabled={submitting}
                aria-label="Folosește camera"
              >
                <span className="document-upload-source-icon" aria-hidden>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                    <circle cx="12" cy="13" r="3" />
                  </svg>
                </span>
                <strong>Folosește camera</strong>
                <span className="document-upload-source-hint">
                  Fotografiază unul sau mai multe documente
                </span>
              </button>
            )}
          </div>
        </section>

        {/* B. FILE LIST --------------------------------------------- */}
        {showFileList && (
          <section className="document-upload-section" aria-labelledby="upload-list-heading">
            <h3 id="upload-list-heading" className="document-upload-section-title">
              {items.length} fișier{items.length === 1 ? '' : 'e'} ·{' '}
              <span className="document-upload-list-total">{formatSize(totalBytes)}</span>
            </h3>

            <ul className="document-upload-list">
              {items.map((row, idx) => {
                const rowStatusClass =
                  row.serverStatus === 'success'
                    ? 'success'
                    : row.serverStatus === 'error' || row.status === 'failed'
                      ? 'error'
                      : row.status === 'processing'
                        ? 'uploading'
                        : row.status === 'ready'
                          ? 'ready'
                          : 'pending';
                return (
                  <li
                    key={row.id}
                    className={`document-upload-list-row document-upload-list-row--${rowStatusClass}`}
                  >
                    <div className="document-upload-list-icon" aria-hidden>
                      <FileTypeIcon name={row.filename} />
                    </div>
                    <div className="document-upload-list-meta">
                      <div className="document-upload-list-name" title={row.filename}>
                        {row.filename}
                      </div>
                      <div className="document-upload-list-sub">
                        <span>{formatSize(row.size)}</span>
                        <PrepBadge
                          row={row}
                          onRetry={() => runOcrForItem(row.id)}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                    {groupMode && items.length > 1 && !submitting && (
                      <div
                        className="document-upload-list-reorder"
                        role="group"
                        aria-label={`Reordonare ${row.filename}`}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => moveItem(row.id, -1)}
                          disabled={idx === 0}
                          aria-label="Mută în sus"
                          title="Mută în sus"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <polyline points="18 15 12 9 6 15" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => moveItem(row.id, 1)}
                          disabled={idx === items.length - 1}
                          aria-label="Mută în jos"
                          title="Mută în jos"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {!submitting && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon document-upload-list-remove"
                        onClick={() => removeItem(row.id)}
                        aria-label={`Elimină ${row.filename}`}
                        title="Elimină"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* C. MODE TOGGLE ------------------------------------------ */}
        {showModeToggle && (
          <section className="document-upload-section" aria-labelledby="upload-mode-heading">
            <h3 id="upload-mode-heading" className="document-upload-section-title">
              Mod de încărcare
            </h3>
            <div
              className="document-upload-mode-pill"
              role="radiogroup"
              aria-label="Mod de încărcare"
            >
              <button
                type="button"
                role="radio"
                aria-checked={!groupMode}
                className={`document-upload-mode-option${!groupMode ? ' is-active' : ''}`}
                onClick={() => setGroupMode(false)}
                disabled={submitting}
              >
                Documente separate
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={groupMode}
                className={`document-upload-mode-option${groupMode ? ' is-active' : ''}`}
                onClick={() => setGroupMode(true)}
                disabled={submitting}
              >
                Un singur document
              </button>
            </div>
            <p className="document-upload-section-hint document-upload-mode-hint">
              {groupMode
                ? `${items.length} fișiere = 1 document grupat (max ${MAX_GROUP_FILES} fișiere)`
                : `${items.length} fișiere = ${items.length} documente`}
            </p>

            {groupMode && (
              <div className="form-group" style={{ marginTop: 'var(--sp-3)' }}>
                <label className="form-label" htmlFor="group-title">
                  Titlu document grupat
                </label>
                <input
                  id="group-title"
                  type="text"
                  className="form-input"
                  value={groupTitle}
                  onChange={(e) => setGroupTitle(e.target.value)}
                  placeholder="Ex: Set analize ianuarie 2026"
                  disabled={submitting}
                  autoComplete="off"
                />
                <p className="document-upload-section-hint" style={{ marginTop: 'var(--sp-2)' }}>
                  Lăsat gol, va fi folosit numele primului fișier.
                </p>
              </div>
            )}
          </section>
        )}

        {/* D. SETĂRI AVANSATE -------------------------------------- */}
        {showFileList && (
          <section className="document-upload-section">
            <button
              type="button"
              className="document-upload-advanced-toggle"
              onClick={() => setAdvancedOpen((v) => !v)}
              aria-expanded={advancedOpen}
              aria-controls="upload-advanced-panel"
              disabled={submitting}
            >
              <svg
                className={`document-upload-advanced-chevron${advancedOpen ? ' is-open' : ''}`}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              Setări avansate
            </button>

            {advancedOpen && (
              <div id="upload-advanced-panel" className="document-upload-advanced-panel">
                {/* E2EE banner (read-only — storage is always E2EE) */}
                <div className="document-upload-e2ee-banner" role="status">
                  <span className="document-upload-e2ee-icon" aria-hidden>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <div>
                    <strong>Criptare E2EE: Activă (întotdeauna)</strong>
                    <p>
                      Stocarea pe disc este mereu criptată end-to-end. Alege mai jos cum vrei să
                      fie extras textul (OCR).
                    </p>
                  </div>
                </div>

                {/* Mod OCR — server vs browser. */}
                <div className="document-upload-advanced-block">
                  <h4 className="document-upload-advanced-subtitle">Mod OCR</h4>
                  <div
                    className="document-upload-ocr-mode"
                    role="radiogroup"
                    aria-label="Mod OCR"
                  >
                    <label
                      className={`document-upload-ocr-mode-option${
                        ocrMode === 'server' ? ' is-active' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name={`${formId}-ocr-mode`}
                        value="server"
                        checked={ocrMode === 'server'}
                        onChange={() => setOcrMode('server')}
                        disabled={submitting}
                      />
                      <span className="document-upload-ocr-mode-title">
                        Procesare pe server <em>(recomandat — mai rapid)</em>
                      </span>
                    </label>
                    <label
                      className={`document-upload-ocr-mode-option${
                        ocrMode === 'client' ? ' is-active' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name={`${formId}-ocr-mode`}
                        value="client"
                        checked={ocrMode === 'client'}
                        onChange={() => setOcrMode('client')}
                        disabled={submitting}
                      />
                      <span className="document-upload-ocr-mode-title">
                        Procesare pe browser <em>(privacy maximă)</em>
                      </span>
                    </label>
                  </div>
                  <p className="document-upload-section-hint">
                    {ocrMode === 'server'
                      ? 'Fișierul este trimis temporar pentru OCR și șters imediat după extragere. Stocarea finală e mereu criptată end-to-end.'
                      : 'OCR-ul rulează în browser. Fișierul nu părăsește niciodată dispozitivul tău în formă necriptată.'}
                  </p>
                </div>

                {/* Pre-procesare locală — only meaningful in browser mode. */}
                <div
                  className={`document-upload-advanced-block${
                    ocrMode === 'server' ? ' is-disabled' : ''
                  }`}
                >
                  <h4 className="document-upload-advanced-subtitle">Pre-procesare locală</h4>
                  <label className="document-upload-checkbox">
                    <input
                      type="checkbox"
                      checked={nerEnabled && ocrMode === 'client'}
                      onChange={(e) => setNerEnabled(e.target.checked)}
                      disabled={submitting || ocrMode === 'server'}
                    />
                    <span>Aplică NER pentru anonimizare PII înainte de criptare</span>
                  </label>
                  <p className="document-upload-section-hint">
                    {ocrMode === 'server'
                      ? 'În modul „pe server" OCR-ul rulează pe backend, deci NER-ul din browser nu se aplică.'
                      : nerEnabled
                        ? 'La prima rulare, modelul NER se descarcă în browser și poate dura mai mult.'
                        : 'Anonimizarea se bazează doar pe reguli (CNP, email, telefon, etichete uzuale).'}
                  </p>

                  {ocrMode === 'client' && nerEnabled && (
                    <div className="document-upload-ner-controls">
                      <div className="form-group">
                        <label className="form-label" htmlFor="ner-preset">
                          Model NER
                        </label>
                        <select
                          id="ner-preset"
                          className="form-input"
                          value={nerPreset}
                          onChange={(e) =>
                            setNerPreset(e.target.value as 'multilingual' | 'ro-bert-pr')
                          }
                          disabled={submitting}
                        >
                          <option value="multilingual">Multilingv (recomandat)</option>
                          <option value="ro-bert-pr">RoBERT-PR (specializat RO)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="ner-min-score">
                          Scor minim încredere: <strong>{nerMinScore.toFixed(2)}</strong>
                        </label>
                        <input
                          id="ner-min-score"
                          className="document-upload-slider"
                          type="range"
                          min={0.5}
                          max={0.99}
                          step={0.01}
                          value={nerMinScore}
                          onChange={(e) => setNerMinScore(Number(e.target.value))}
                          disabled={submitting}
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-ghost btn-sm document-upload-more-options"
                        onClick={() => setShowNerAdvanced((v) => !v)}
                        disabled={submitting}
                        aria-expanded={showNerAdvanced}
                      >
                        {showNerAdvanced ? 'Mai puține opțiuni' : 'Mai multe opțiuni'}
                      </button>

                      {showNerAdvanced && (
                        <div className="document-upload-ner-advanced">
                          <div className="form-group">
                            <label className="form-label" htmlFor="ner-revision">
                              revision
                            </label>
                            <input
                              id="ner-revision"
                              className="form-input"
                              value={nerRevision}
                              onChange={(e) => setNerRevision(e.target.value)}
                              disabled={submitting}
                              placeholder="main / refs/pr/2"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label" htmlFor="ner-dtype">
                              dtype
                            </label>
                            <select
                              id="ner-dtype"
                              className="form-input"
                              value={nerDtype}
                              onChange={(e) =>
                                setNerDtype(e.target.value as 'fp32' | 'q8' | 'auto')
                              }
                              disabled={submitting}
                            >
                              <option value="fp32">fp32</option>
                              <option value="q8">q8</option>
                              <option value="auto">auto</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label" htmlFor="ner-model-file">
                              model file
                            </label>
                            <input
                              id="ner-model-file"
                              className="form-input"
                              value={nerModelFileName}
                              onChange={(e) => setNerModelFileName(e.target.value)}
                              disabled={submitting}
                              placeholder="model"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Previzualizare conținut OCR */}
                <div className="document-upload-advanced-block">
                  <h4 className="document-upload-advanced-subtitle">Previzualizare conținut OCR</h4>
                  <div className="document-upload-preview-row">
                    <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
                      <label className="form-label" htmlFor="preview-file">
                        Fișier
                      </label>
                      <select
                        id="preview-file"
                        className="form-input"
                        value={previewFileId ?? ''}
                        onChange={(e) => setPreviewFileId(e.target.value || null)}
                        disabled={submitting || items.length === 0}
                      >
                        <option value="">— alege fișier —</option>
                        {items.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.filename}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="document-upload-preview-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={previewView === 'source'}
                        className={`document-upload-preview-tab${
                          previewView === 'source' ? ' is-active' : ''
                        }`}
                        onClick={() => setPreviewView('source')}
                      >
                        Sursă
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={previewView === 'preview'}
                        className={`document-upload-preview-tab${
                          previewView === 'preview' ? ' is-active' : ''
                        }`}
                        onClick={() => setPreviewView('preview')}
                      >
                        Previzualizare
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const it = previewFileId ? items.find((i) => i.id === previewFileId) : null;
                    if (!it) {
                      return (
                        <div className="document-upload-preview-empty">
                          <span>Selectează un fișier pentru a vedea previzualizarea.</span>
                        </div>
                      );
                    }
                    if (!it.ocr) {
                      return (
                        <div className="document-upload-preview-empty">
                          {ocrMode === 'server' ? (
                            <span>
                              În modul „pe server" OCR-ul rulează după încărcare. Comută pe
                              „pe browser" pentru a vedea o previzualizare locală.
                            </span>
                          ) : it.status === 'processing' ? (
                            <span>Se procesează…</span>
                          ) : (
                            <>
                              <span>Apasă "Procesează" pentru a vedea conținutul.</span>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => runOcrForItem(it.id)}
                                disabled={submitting}
                              >
                                Procesează
                              </button>
                            </>
                          )}
                          {it.error && (
                            <div className="document-upload-preview-error">{it.error}</div>
                          )}
                        </div>
                      );
                    }
                    return previewView === 'source' ? (
                      <textarea
                        className="form-textarea document-upload-textarea"
                        value={it.ocr.markdown}
                        readOnly
                        rows={12}
                      />
                    ) : (
                      <div className="card document-upload-preview-rendered">
                        <ReactMarkdown
                          className="md-content"
                          remarkPlugins={[remarkGfm]}
                          urlTransform={(url, key) => {
                            if (
                              key === 'src' &&
                              typeof url === 'string' &&
                              url.startsWith('data:image/')
                            ) {
                              return url;
                            }
                            return defaultUrlTransform(url);
                          }}
                        >
                          {it.ocr.markdown}
                        </ReactMarkdown>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </section>
        )}

        {/* E. NOTES ------------------------------------------------- */}
        {showFileList && (
          <section
            className="document-upload-section document-upload-section--details"
            aria-labelledby="upload-notes-heading"
          >
            <h3 id="upload-notes-heading" className="document-upload-section-title">
              Note <span className="document-upload-optional">(opțional)</span>
            </h3>
            <div className="form-group document-upload-notes">
              <textarea
                id="doc-notes"
                className="form-textarea document-upload-textarea"
                placeholder="Observații scurte despre document(e)…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                rows={3}
              />
            </div>
          </section>
        )}

        {submitting && (
          <div className="document-upload-progress" aria-live="polite">
            <div className="document-upload-progress-head">
              <span className="document-upload-progress-label">
                {progressLabel || 'Se încarcă…'}
              </span>
              <span className="document-upload-progress-pct">{progress}%</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </form>

      {/* F. FOOTER ------------------------------------------------- */}
      <ModalFooter>
        <div className="form-actions app-modal-footer-actions document-upload-footer-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            {successMsg && !anyFailedServer && !anyFailedLocal ? 'Închide' : 'Anulează'}
          </button>
          {(anyFailedServer || anyFailedLocal) && !submitting && (
            <button type="button" className="btn btn-secondary" onClick={handleRetryFailed}>
              Retrimite eșuate
            </button>
          )}
          <button
            type="submit"
            form={formId}
            className="btn btn-primary document-upload-submit"
            disabled={submitting || validationError !== null}
            title={validationError || undefined}
          >
            {submitting
              ? 'Se încarcă…'
              : groupMode
                ? items.length >= 2
                  ? 'Încarcă document grupat'
                  : 'Încarcă document'
                : items.length === 1
                  ? 'Încarcă documentul'
                  : `Încarcă ${items.length || ''} documente`.trim()}
          </button>
        </div>
      </ModalFooter>
    </>
  );
};

// ---------------------------------------------------------------------------
// Presentational helpers.
// ---------------------------------------------------------------------------

const FileTypeIcon: React.FC<{ name: string }> = ({ name }) => {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) {
    return (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="15" y2="17" />
      </svg>
    );
  }
  if (
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.tiff') ||
    lower.endsWith('.tif') ||
    lower.endsWith('.heic') ||
    lower.endsWith('.heif')
  ) {
    return (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </svg>
    );
  }
  if (lower.endsWith('.xlsx') || lower.endsWith('.csv')) {
    return (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
        <line x1="8" y1="21" x2="16" y2="21" />
      </svg>
    );
  }
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
};

/** Per-row status pill. Combines local pre-processing state with the
 *  server's verdict from the most recent submit:
 *   - server success → "Încărcat" ✓
 *   - server error   → "Eroare: …" ✗ (with retry button outside)
 *   - local failed   → "Pre-procesare eșuată" ⚠ + inline retry
 *   - processing     → "Se procesează…"
 *   - ready          → 🔒 "Criptat local" (tooltip explains)
 *   - queued         → "În așteptare"
 */
const PrepBadge: React.FC<{
  row: FilePrep;
  onRetry: () => void;
  disabled: boolean;
}> = ({ row, onRetry, disabled }) => {
  if (row.serverStatus === 'success') {
    return (
      <span className="document-upload-status document-upload-status--success">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>{' '}
        Încărcat
      </span>
    );
  }
  if (row.serverStatus === 'error') {
    return (
      <span
        className="document-upload-status document-upload-status--error"
        title={row.serverMessage || 'Eroare la încărcare'}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>{' '}
        {row.serverMessage ? `Eroare: ${row.serverMessage}` : 'Eroare'}
      </span>
    );
  }
  if (row.status === 'failed') {
    return (
      <>
        <span
          className="document-upload-status document-upload-status--error"
          title={row.error || 'Pre-procesare eșuată'}
        >
          <span aria-hidden>⚠</span> Pre-procesare eșuată
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm document-upload-status-retry"
          onClick={onRetry}
          disabled={disabled}
        >
          Reîncearcă
        </button>
      </>
    );
  }
  if (row.status === 'processing') {
    return (
      <span className="document-upload-status document-upload-status--uploading">
        <span className="document-upload-status-dot" aria-hidden /> Se procesează…
      </span>
    );
  }
  if (row.status === 'ready') {
    return (
      <span
        className="document-upload-status document-upload-status--ready"
        title="Criptat local, gata de încărcare."
      >
        <span aria-hidden>🔒</span> Criptat local
      </span>
    );
  }
  return (
    <span className="document-upload-status document-upload-status--pending">În așteptare</span>
  );
};

export default DocumentUpload;
